import { type FunctionComponent, useEffect, useState } from "react";

import { usePostiesQuizContext } from "scmp-app/components/posties/quiz/contexts";
import { PostiesQuizEndPage } from "scmp-app/components/posties/quiz/quiz-end-page";
import { PostiesQuizQuestions } from "scmp-app/components/posties/quiz/quiz-questions";
import { PostiesQuizStartPage } from "scmp-app/components/posties/quiz/quiz-start-page";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { useCurrentArticleIds } from "scmp-app/lib/current-item/hooks";
import { useIntersection } from "scmp-app/lib/hooks";

import { Container, QuizTimeLabel, Wrapper } from "./styles";

type Props = {
  className?: string;
};
export const PostiesQuizWidget: FunctionComponent<Props> = ({ className }) => {
  const { quizReference, quizStatus } = usePostiesQuizContext();
  const { captureIntersectionElement, intersection } = useIntersection({ threshold: 0.5 });
  const { currentArticleId } = useCurrentArticleIds();
  const [quizWidgetImpressionSent, setQuizWidgetImpressionSent] = useState(false);
  const [monitorFinishImpression, setMonitorFinishImpression] = useState(false);

  const showStartPage = quizStatus === "NOT_STARTED" || quizStatus === "RESUME";
  const showQuestions = quizStatus === "IN_PROGRESS" || quizStatus === "REVIEW";
  const showEndPage = quizStatus === "FINISHED";

  useEffect(() => {
    if (!intersection?.isIntersecting || quizWidgetImpressionSent) return;
    setQuizWidgetImpressionSent(true);

    const ctaType = (() => {
      switch (quizStatus) {
        case "FINISHED":
          return "finish";
        case "RESUME":
          return "resume";
        default:
          return "start";
      }
    })();

    sendGA4Tracking({
      action: "imp",
      category: "posties",
      customized_parameters: {
        article_id: currentArticleId,
        cta_type: ctaType,
      },
      subcategory: "quiz",
    });
  }, [intersection, quizWidgetImpressionSent, currentArticleId, quizStatus]);

  useEffect(() => {
    if (quizStatus === "IN_PROGRESS") {
      setMonitorFinishImpression(true);
      return;
    }
    if (!monitorFinishImpression) return;
    if (quizStatus === "FINISHED") {
      setMonitorFinishImpression(false);
      sendGA4Tracking({
        action: "imp",
        category: "posties",
        customized_parameters: {
          article_id: currentArticleId,
          cta_type: "finish",
        },
        subcategory: "quiz",
      });
    }
  }, [currentArticleId, monitorFinishImpression, quizStatus]);

  return (
    <Container className={`${className} student-quiz`} ref={quizReference}>
      {showStartPage && <QuizTimeLabel />}
      <Wrapper ref={captureIntersectionElement}>
        {showStartPage && <PostiesQuizStartPage />}

        {showQuestions && <PostiesQuizQuestions />}

        {showEndPage && <PostiesQuizEndPage />}
      </Wrapper>
    </Container>
  );
};

PostiesQuizWidget.displayName = "PostiesQuizWidget";
